const MAX_EDGE = 2048;
const JPEG_QUALITY = 0.75;
// Skip compression for files already under this size AND within pixel bounds.
// Anything above gets JPEG-compressed regardless of pixel dimensions.
const SMALL_FILE_BYTES = 300_000; // 300 KB

export async function resizeImage(file: File): Promise<File> {
  // Only process actual images
  if (!file.type.startsWith("image/")) return file;

  const bitmap = await createImageBitmap(file);
  const { width, height } = bitmap;

  const needsResize = width > MAX_EDGE || height > MAX_EDGE;
  const needsCompress = file.size > SMALL_FILE_BYTES || file.type === "image/png";

  // Small JPEGs within pixel bounds: pass through as-is
  if (!needsResize && !needsCompress) {
    bitmap.close();
    return file;
  }

  const scale = needsResize ? MAX_EDGE / Math.max(width, height) : 1;
  const newW = Math.round(width * scale);
  const newH = Math.round(height * scale);

  const canvas = new OffscreenCanvas(newW, newH);
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(bitmap, 0, 0, newW, newH);
  bitmap.close();

  const blob = await canvas.convertToBlob({
    type: "image/jpeg",
    quality: JPEG_QUALITY,
  });

  // Preserve the original name but with .jpg extension
  const name = file.name.replace(/\.[^.]+$/, ".jpg");
  return new File([blob], name, { type: "image/jpeg" });
}
